import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Button, VStack, Text, Box, Icon, Badge, HStack} from "@chakra-ui/react";
import {Check} from 'lucide-react';
import objectUtil from "@/utils/objectUtil";

export const NestedCheckboxMultiSelect = ({
                                              items,
                                              getSelectedText,
                                              selectedItems,
                                              onChange,
                                              placeholder,
                                              variant = 'colorful',
                                              dropdownProps = {},
                                              ...props
                                          }) => {
    /** Allows to forbid first state of selectedItems triggering onChange */
    const [state, setState] = useState(0);
    const [_selectedItems, _setSelectedItems] = useState(selectedItems ?? []);
    const [isOpen, setIsOpen] = useState(false);
    const id = `checkbox-multiselect-${Date.now()}`;
    const containerRef = useRef();

    useEffect(() => {
        const clickListener = (event) => {
            if (containerRef?.current && !containerRef.current.contains(event.target)) {
                setIsOpen(() => false);
            }
        }

        window.addEventListener('click', clickListener)

        return () => {
            window.removeEventListener('click', clickListener);
        }
    }, []);

    /*
    const getChildrenValues = (item) => {
        const result = [];
        for (const current of (item?.children ?? [])) {
            result.push(current.value);
            result.push(...getChildrenValues(current));
        }
        return result;
    }

    const getValues = (nodes, parents = []) => {
        let result = {}
        for (const item of nodes) {
            result[item.value] = {
                children: getChildrenValues(item),
                parents: [...parents]
            }
            if (item.children) {
                result = {
                    ...result,
                    ...getValues(item.children, [...parents, item.value])
                }
            }
        }
        return result;
    };
     */

    /** Allows to generate object
     * containing all children values and all parent values
     *

     const values = useMemo(() => {
     return getValues(items);
     }, [items]);

     * */

    const _onChange = (value, event) => {
        event.stopPropagation();
        _setSelectedItems(prevState => {
            /** This is a system to select parent and children automatically

            const _values = values[value];
            let include = !prevState.includes(value);
            let result = Array.from(new Set([...prevState, value, ..._values.children]));
            if (!include) {
                result = result.filter((v) => ![value, ..._values.children].includes(v));
            }
            // In reverse cause the closest parent need to be checked to check the most far one
            for(const parent of _values.parents.reverse()){
                const children = values[parent].children;
                result = Array.from(new Set([...result, parent]));
                if(!children.every((val) => result.includes(val))){
                    result = result.filter((v) => ![parent].includes(v));
                }
            }
             */
            return objectUtil.toggleElement([...prevState], value);
        });
    }

    useEffect(() => {
        if (state === 0) {
            setState(() => 1)
        } else {
            onChange(_selectedItems);
        }
    }, [_selectedItems]);

    const isSelected = useCallback((value) => {
        return _selectedItems.includes(value);
    }, [_selectedItems]);

    const renderChildren = useCallback((children, depth = 0) => {
        return children.map((e) => <VStack
            key={`multiselect-${e.value}-${id}`}
            w="100%"
            pl={`${0.2 + depth}rem`}
            position="relative"
            gap={0}
        >
            {
                depth > 0
                && <Box
                    position="absolute"
                    left={`${0.2 + depth}rem`}
                    h="100%"
                    w="1px"
                    zIndex={2}
                />
            }
            <Button
                w="100%"
                onClick={(event) => _onChange(e.value, event)}
                bg={"white"}
            >
                <HStack flex={1}>
                    {
                        e?.color
                            ?
                            <Badge
                                bg={e.color.background}
                                color={e.color.text}
                            >
                                {e.label}
                            </Badge>
                            :
                            <Text>{e.label}</Text>

                    }
                </HStack>
                <Box position="relative" ml="auto" maxH="1rem" maxW="1rem" minH="1rem" minW="1rem" marginLeft="1rem"
                     borderWidth=".15rem" borderRadius=".15rem" borderColor="gray.500">
                    {isSelected(e.value) && <Icon as={Check} position="absolute" w="100%" h="100%" left={0} color="secondary.500"/>}
                </Box>
            </Button>
            {
                e.children.length > 0
                && renderChildren(e.children, depth + 1)
            }
        </VStack>)
    }, [_selectedItems])

    return <VStack position="relative" justify="center" ref={containerRef}>
        <Button bg={"secondary.500"} color={'white'} w="100%" variant={variant} onClick={() => setIsOpen(prevState => !prevState)} {...props}>
            {(_selectedItems?.length > 0) ? getSelectedText(_selectedItems) : placeholder}
        </Button>
        {isOpen &&
            <VStack onClick={(e) => e.stopPropagation()} maxH="50vh" overflowY="auto" overflowX="hidden" left="10%" w="80%" top="3rem" position="absolute" bg="white" color="black"
                    borderRadius="0 0 .5rem .5rem" gap="0" {...dropdownProps}>
                {
                    renderChildren(items)
                }
            </VStack>}
    </VStack>;
};

export default NestedCheckboxMultiSelect;
